import { ReactNode } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import {
  Table,
  CombinedTable,
  TableAvailability,
  TableWeekdayAvailability,
  Reservation,
} from "@/types/table";
import {
  DashboardReservation,
  ReservationStatusInfo,
  SendReservationEmailPayload,
} from "@/types/reservation";
import { ReservationPreviewContent } from "@/components/ReservationPreviewContent";

interface ReservationPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reservation: DashboardReservation | null;
  endTime: string;
  tables: Table[];
  combinedTables: CombinedTable[];
  convertedReservations: Reservation[];
  availabilities: TableAvailability[];
  weekdayAvailabilities: TableWeekdayAvailability[];
  getReservationStatus: (reservation: DashboardReservation) => ReservationStatusInfo;
  getLoyaltyBadge: (email: string | undefined) => ReactNode;
  getNoShowBadge: (email: string | undefined) => ReactNode;
  getCustomerNotes: (email: string | undefined) => string | null;
  onUpdateStatus: (
    reservationId: number,
    status: "arrivé" | "annulé" | "planifié",
    cancellationReason?: string,
  ) => void;
  onEdit: (reservation: DashboardReservation) => void;
  onQuickTableChange: (
    reservationId: number,
    tableId: string | null,
    combinedTableId: string | null,
  ) => void;
  onSendEmail: (data: SendReservationEmailPayload) => void;
  sendingEmail: number | null;
  onSwapSelect?: (reservation: DashboardReservation) => void;
  onSwapTables?: (aSupabaseId: string, bSupabaseId: string) => Promise<void> | void;
  reservationSettings?: {
    bookingMode: "slot" | "time";
    reservationDurationMinutes: number;
  };
}

/**
 * Unified reservation preview dialog.
 *
 * Opened on click from every reservation surface (list, timeline, floor plan,
 * client history, search). "Modifier" button forwards to EditReservationDialog
 * via the onEdit callback.
 *
 * Visual parity with TimelineReservationPopover is guaranteed by sharing
 * ReservationPreviewContent.
 */
export const ReservationPreviewDialog = ({
  open,
  onOpenChange,
  reservation,
  onEdit,
  ...contentProps
}: ReservationPreviewDialogProps) => {
  if (!reservation) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 w-full sm:max-w-md max-w-[calc(100vw-2rem)] overflow-hidden">
        <DialogHeader className="sr-only">
          <VisuallyHidden>
            <DialogTitle>Aperçu de la réservation de {reservation.name}</DialogTitle>
          </VisuallyHidden>
        </DialogHeader>
        <ReservationPreviewContent
          reservation={reservation}
          onEdit={(r) => {
            onOpenChange(false);
            onEdit(r);
          }}
          {...contentProps}
        />
      </DialogContent>
    </Dialog>
  );
};
